import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '../../lib/utils';
import { RECRUITER_NAV_ITEMS, RECRUITER_NAV_SECTIONS, resolveRecruiterNavKey, RecruiterNavKey } from './recruiterNavItems';

const COLLAPSE_KEY = 'cevee_recruiter_sidebar_collapsed';
const ACTIVE_MARKER = 'bg-slate-900';

interface SidebarNavProps {
  activeKey: RecruiterNavKey | null;
  collapsed?: boolean;
  onNavigate?: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ activeKey, collapsed, onNavigate }) => {
  const { t } = useTranslation('common');
  return (
    <nav className="flex flex-col px-3">
      {RECRUITER_NAV_SECTIONS.map((section, sectionIdx) => {
        const items = RECRUITER_NAV_ITEMS.filter((i) => i.section === section.id);
        if (items.length === 0) return null;
        return (
          <div key={section.id} className={sectionIdx > 0 ? 'mt-4' : ''}>
            {collapsed
              ? sectionIdx > 0 && <div className="mx-auto mb-3 h-px w-6 bg-slate-200" />
              : (
                <p className="px-1 pb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t(section.labelKey)}
                </p>
              )}
            <div className="flex flex-col gap-0.5">
              {items.map(({ key, to, labelKey, Icon }) => {
                const isActive = key === activeKey;
                return (
                  <Link
                    key={key}
                    to={to}
                    onClick={onNavigate}
                    title={collapsed ? t(labelKey) : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'group relative flex items-center rounded-lg text-sm transition-colors',
                      collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2',
                      isActive
                        ? 'bg-slate-100 font-medium text-slate-900'
                        : 'font-medium text-slate-500 hover:bg-slate-100/70 hover:text-slate-900',
                    )}
                  >
                    {isActive && (
                      <span
                        className={cn('absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full', ACTIVE_MARKER)}
                        aria-hidden
                      />
                    )}
                    <Icon
                      className={cn(
                        'h-[18px] w-[18px] flex-shrink-0 transition-colors',
                        isActive
                          ? 'text-slate-900'
                          : 'text-slate-400 group-hover:text-slate-700',
                      )}
                    />
                    {!collapsed && (
                      <span className={cn('truncate tracking-tight', isActive && 'font-semibold text-slate-900')}>
                        {t(labelKey)}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
};

/**
 * Professional B2B app shell for recruiter routes: a fixed left sidebar on
 * desktop (collapsible to an icon rail) and a slide-in drawer on mobile. Sits
 * below the global Header. Gated behind the `cevee_recruiter_shell` flag.
 */
const RecruiterShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { t } = useTranslation('common');
  const activeKey = resolveRecruiterNavKey(location.pathname, location.search);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Close the mobile drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="flex-1 w-full">
      {/* Desktop sidebar — fixed under the global header, collapsible */}
      <aside
        className={cn(
          'fixed left-0 top-11 bottom-0 z-40 hidden flex-col border-r border-slate-200 bg-slate-50 transition-[width] duration-200 sm:top-[60px] lg:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <div className={`flex items-center pt-4 pb-2 ${collapsed ? 'justify-center px-2' : 'justify-end px-3'}`}>
          <button
            type="button"
            onClick={toggleCollapsed}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-700 transition-colors"
            aria-label={collapsed ? t('recruiterNav.expand') : t('recruiterNav.collapse')}
            title={collapsed ? t('recruiterNav.expand') : t('recruiterNav.collapse')}
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto pb-6">
          <SidebarNav activeKey={activeKey} collapsed={collapsed} />
        </div>
      </aside>

      {/* Mobile drawer trigger bar */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium tracking-tight text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          aria-label={t('recruiterNav.menu')}
        >
          <Menu className="h-5 w-5" />
          <span>{t('recruiterNav.menu')}</span>
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[10001] lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 flex w-64 flex-col border-r border-slate-200 bg-slate-50 shadow-xl">
            <div className="flex items-center justify-between px-5 pb-3 pt-5">
              <span className="text-sm font-semibold tracking-tight text-slate-900">{t('appName')}</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                aria-label={t('recruiterNav.menu')}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto pb-6">
              <SidebarNav activeKey={activeKey} onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={collapsed ? 'lg:pl-16' : 'lg:pl-60'}>{children}</div>
    </div>
  );
};

export default RecruiterShell;
